import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PaginatedResult, PaginationParams } from '@core/common/models/pagination.models';
import {
  Product,
  ProductMutationResult,
  ProductSupplySourceInputValue,
  ProductVariantBatchMutationResult,
  ProductVariantMutationResult,
  SyncProductSupplySourcesResult,
} from '@features/catalog/models/product.models';
import {
  CreateProductBundleInput,
  ProductAttributeLinkMutationResult,
  ProductModifierLinkMutationResult,
  UpdateProductBundleInput,
  UpsertProductVariantsInput,
} from '@features/catalog/models/product-input.models';
import { ProductListFilters } from '@features/catalog/models/product-list-filters.models';
import { ProductApiService } from '@features/catalog/data-access/product-api.service';
import { PreparationSettingsApiInput } from '@shared/ui/preparation-editor/preparation-editor.models';

@Injectable()
export class CatalogProductService {
  constructor(private readonly productApi: ProductApiService) {}

  list(
    filters?: ProductListFilters,
    pagination?: PaginationParams
  ): Observable<PaginatedResult<Product>> {
    return this.productApi.list(filters, pagination);
  }

  searchByName(name: string, listFilters?: ProductListFilters): Observable<PaginatedResult<Product>> {
    return this.productApi.searchByName(name, listFilters);
  }

  destroy(id: string): Observable<ProductMutationResult> {
    return this.productApi.destroy(id);
  }

  deactivate(id: string): Observable<ProductMutationResult> {
    return this.productApi.destroy(id);
  }

  reactivate(id: string): Observable<ProductMutationResult> {
    return this.productApi.updateActive(id, true);
  }

  permanentlyDelete(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.productApi.permanentlyDelete(id);
  }

  bulkUpdateProducts(
    ids: readonly string[],
    changes: { active?: boolean; posVisible?: boolean; publicVisible?: boolean }
  ): Observable<{ updatedCount: number; errors: string[] }> {
    return this.productApi.bulkUpdateProducts(ids, changes);
  }

  getById(id: string): Observable<Product | null> {
    return this.productApi.getById(id);
  }

  createBundle(input: CreateProductBundleInput): Observable<ProductMutationResult> {
    return this.productApi.createBundle(input);
  }

  updateBundle(input: UpdateProductBundleInput): Observable<ProductMutationResult> {
    return this.productApi.updateBundle(input);
  }

  /**
   * `createProductBundle`/`updateProductBundle` no aceptan peso/dimensiones (el backend
   * los expone solo en `updateProduct`), por eso se persisten con una llamada aparte.
   */
  updateDimensions(
    id: string,
    dimensions: {
      weightGrams?: number | null;
      lengthCm?: number | null;
      widthCm?: number | null;
      heightCm?: number | null;
    }
  ): Observable<ProductMutationResult> {
    return this.productApi.updateDimensions(id, dimensions);
  }

  upsertVariants(input: UpsertProductVariantsInput): Observable<ProductVariantBatchMutationResult> {
    return this.productApi.upsertVariants(input);
  }

  syncSupplySources(
    productId: string,
    supplySources: readonly ProductSupplySourceInputValue[]
  ): Observable<SyncProductSupplySourcesResult> {
    return this.productApi.syncSupplySources(productId, supplySources);
  }

  destroyVariant(id: string): Observable<ProductVariantMutationResult> {
    return this.productApi.destroyVariant(id);
  }

  removeAttributeDefinitionFromProduct(input: {
    productId: string;
    productAttributeDefinitionId: string;
  }): Observable<ProductAttributeLinkMutationResult> {
    return this.productApi.removeAttributeDefinitionFromProduct(input);
  }

  removeAttributeValueFromVariant(input: {
    productVariantId: string;
    productAttributeValueId: string;
  }): Observable<ProductAttributeLinkMutationResult> {
    return this.productApi.removeAttributeValueFromVariant(input);
  }

  assignModifierGroupDefinitionToProduct(input: {
    productId: string;
    productModifierGroupDefinitionId: string;
  }): Observable<ProductModifierLinkMutationResult> {
    return this.productApi.assignModifierGroupDefinitionToProduct(input);
  }

  removeModifierGroupDefinitionFromProduct(input: {
    productId: string;
    productModifierGroupDefinitionId: string;
  }): Observable<ProductModifierLinkMutationResult> {
    return this.productApi.removeModifierGroupDefinitionFromProduct(input);
  }

  bulkUpdatePreparation(
    ids: readonly string[],
    input: PreparationSettingsApiInput
  ): Observable<{ updatedCount: number; errors: string[] }> {
    return this.productApi.bulkUpdatePreparation(ids, input);
  }
}
